// Client texts: the thread with one practice contact, in a modal over Practice
// Details (docs/client-texts.md). Opening it reads the thread with mark_read=1;
// Refresh re-reads (no polling in v1). Sending is always a click here: a Claude
// session may leave a draft, which only prefills the compose box.
//
// An inbound body is the client's words and may name a patient, so it is shown
// here and nowhere else: nothing in this file logs or copies the thread.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { SmsMessage } from "../outreach/SmsNumberParts";
import { MAX_BODY_LENGTH, fetchThread, sendMessage } from "./api";
import "./ChatPane.css";

const STATUS_LABELS = {
  queued: "queued",
  sent: "sent",
  delivered: "delivered",
  failed: "failed",
  received: "received",
};

export function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function Bubble({ message }) {
  const out = message.direction === "out";
  const status = out ? STATUS_LABELS[message.status] || message.status : null;
  return (
    <div className={`ct-bubble-row ct-bubble-row-${out ? "out" : "in"}`}>
      <div className={`ct-bubble ct-bubble-${out ? "out" : "in"}`}>
        <div className="ct-bubble-body">{message.body}</div>
        <div className="ct-bubble-meta">
          {out && message.sentByName && <span className="ct-bubble-sender">{message.sentByName}</span>}
          <span className="ct-bubble-time">{formatTime(message.sentAt || message.receivedAt || message.createdAt)}</span>
          {status && <span className={`ct-status ct-status-${message.status || "unknown"}`}>{status}</span>}
          {out && message.testMode && <span className="ct-status ct-status-test">test</span>}
        </div>
      </div>
    </div>
  );
}

export default function ChatPane({ practiceId, practiceName, contactId, contactName, onClose }) {
  const [thread, setThread] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [body, setBody] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const bodyRef = useRef("");
  bodyRef.current = body;
  const aliveRef = useRef(true);

  const load = useCallback(
    async ({ initial }) => {
      if (!initial) setRefreshing(true);
      try {
        const t = await fetchThread(practiceId, contactId, { markRead: true });
        if (!aliveRef.current) return;
        setThread(t);
        setLoadError(null);
        // A draft fills an empty compose box only; what the person typed stays.
        if (t.draft && !bodyRef.current.trim()) {
          setBody(t.draft.body);
          setDraftId(t.draft.id);
        }
      } catch (err) {
        if (!aliveRef.current) return;
        setLoadError(err.message || "Could not load the thread.");
      } finally {
        if (aliveRef.current) setRefreshing(false);
      }
    },
    [practiceId, contactId],
  );

  useEffect(() => {
    aliveRef.current = true;
    setThread(null);
    setLoadError(null);
    setBody("");
    setDraftId(null);
    setSendError(null);
    load({ initial: true });
    return () => {
      aliveRef.current = false;
    };
  }, [load]);

  const messageCount = thread ? thread.messages.length : 0;
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messageCount]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const clearDraft = () => {
    setBody("");
    setDraftId(null);
    if (textareaRef.current) textareaRef.current.focus();
  };

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    if (text.length > MAX_BODY_LENGTH) {
      setSendError(`A text is at most ${MAX_BODY_LENGTH.toLocaleString()} characters.`);
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      const row = await sendMessage(practiceId, { contactId, body: text, draftId });
      if (!aliveRef.current) return;
      setThread((t) => (t ? { ...t, messages: [...t.messages, row] } : t));
      setBody("");
      setDraftId(null);
    } catch (err) {
      if (!aliveRef.current) return;
      setSendError(err.message || "Could not send the text.");
    } finally {
      if (aliveRef.current) setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  const contact = thread && thread.contact;
  const name = (contact && contact.name) || contactName || "Contact";
  const title = practiceName ? `${name} · ${practiceName}` : name;
  const fromDraft = draftId !== null && body.trim().length > 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="create-modal ct-pane"
        role="dialog"
        aria-modal="true"
        aria-label={`Texts with ${name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ct-pane-head">
          <div>
            <h2 className="ct-pane-title">{title}</h2>
            <div className="ct-pane-sub">
              {contact && contact.cell ? (
                <span className="ct-pane-cell">{contact.cell}</span>
              ) : (
                <span className="sms-muted">{thread ? "no number on file" : "Loading…"}</span>
              )}
              {contact && !contact.active && <span className="ct-status ct-status-inactive">inactive</span>}
            </div>
          </div>
          <div className="ct-pane-head-actions">
            <button
              type="button"
              className="sms-btn sms-btn-small"
              onClick={() => load({ initial: false })}
              disabled={refreshing || !thread}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button type="button" className="ct-pane-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>

        <SmsMessage tone="error" onDismiss={() => setLoadError(null)}>
          {loadError}
        </SmsMessage>

        <div className="ct-thread" ref={listRef}>
          {!thread && !loadError ? (
            <p className="sms-muted">Loading…</p>
          ) : thread && thread.messages.length === 0 ? (
            <p className="sms-muted ct-thread-empty">No texts yet.</p>
          ) : (
            thread && thread.messages.map((m, i) => <Bubble key={m.id ?? `m-${i}`} message={m} />)
          )}
        </div>

        <div className="ct-compose">
          {fromDraft && (
            <div className="ct-draft-note">
              <span>Suggested by Claude</span>
              <button type="button" className="ct-link" onClick={clearDraft} disabled={sending}>
                Clear
              </button>
            </div>
          )}
          <SmsMessage tone="error" onDismiss={() => setSendError(null)}>
            {sendError}
          </SmsMessage>
          <textarea
            ref={textareaRef}
            className="sms-input ct-textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Text ${name}…`}
            maxLength={MAX_BODY_LENGTH}
            rows={3}
            disabled={sending || !thread}
            aria-label="Message"
          />
          <div className="ct-compose-foot">
            <span className="ct-counter">
              {body.length.toLocaleString()} / {MAX_BODY_LENGTH.toLocaleString()} · Cmd/Ctrl+Enter sends
            </span>
            <button
              type="button"
              className="sms-btn sms-btn-primary"
              onClick={send}
              disabled={sending || !thread || !body.trim()}
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
